/**
 * Turns raw agent tool executions (MCP calls and built-in tools) into plain
 * English for the Chat / Command / Routine Details panel:
 *
 *   mcp__nowcerts__search_insureds {name:"Smith"} -> [{...},{...}]
 *     => title   "Searched insureds for “Smith”"
 *        outcome "Found 2 results"
 *        source  "Nowcerts"
 *
 * The raw tool name, inputs and result stay available underneath for anyone
 * who wants the technical view; this only decides what the summary row says.
 */

export interface ToolStepSummary {
  /** One short sentence: what the agent did. */
  title: string;
  /** One short sentence: what came of it. */
  outcome: string;
  /** Connected app / MCP server the tool belongs to, when the name says so. */
  source?: string;
  ok: boolean;
  running: boolean;
}

// Tools whose purpose isn't obvious from verb + noun.
const KNOWN_TOOLS: Record<string, string> = {
  execute_sql_query: 'Queried the agency database',
  run_sql: 'Queried the agency database',
  sql_query: 'Queried the agency database',
  get_query: 'Pulled data from the agency database',
  intent_classifier: 'Worked out what was being asked',
  workflow_router: 'Chose the right workflow',
  summary_writer: 'Wrote the summary',
  send_email: 'Sent an email',
  image_generate: 'Generated an image',
  thinking: 'Thought it through',
  web_search: 'Searched the web',
  web_fetch: 'Read a web page',
};

const VERBS: Record<string, string> = {
  get: 'Looked up', fetch: 'Looked up', read: 'Looked up', retrieve: 'Looked up', load: 'Looked up', lookup: 'Looked up',
  search: 'Searched', find: 'Searched', query: 'Searched', filter: 'Searched',
  list: 'Pulled a list of',
  create: 'Created', add: 'Added', insert: 'Added', new: 'Created',
  update: 'Updated', edit: 'Updated', patch: 'Updated', set: 'Updated', modify: 'Updated', save: 'Saved',
  delete: 'Removed', remove: 'Removed',
  send: 'Sent', email: 'Emailed', notify: 'Notified',
  execute: 'Ran', run: 'Ran', call: 'Called',
  generate: 'Generated', draft: 'Drafted', write: 'Wrote', summarize: 'Summarized',
  upload: 'Uploaded', download: 'Downloaded', import: 'Imported', export: 'Exported',
  classify: 'Classified', check: 'Checked', validate: 'Checked', verify: 'Checked',
  calculate: 'Calculated', compute: 'Calculated', compare: 'Compared',
  schedule: 'Scheduled', assign: 'Assigned', attach: 'Attached', link: 'Linked',
};

// Present tense, for steps that failed: "Tried to create quote".
const PRESENT: Record<string, string> = {
  'Looked up': 'look up', Searched: 'search', 'Pulled a list of': 'pull a list of', Created: 'create', Added: 'add',
  Updated: 'update', Saved: 'save', Removed: 'remove', Sent: 'send', Emailed: 'email', Notified: 'notify', Ran: 'run',
  Called: 'call', Generated: 'generate', Drafted: 'draft', Wrote: 'write', Summarized: 'summarize', Uploaded: 'upload',
  Downloaded: 'download', Imported: 'import', Exported: 'export', Classified: 'classify', Checked: 'check',
  Calculated: 'calculate', Compared: 'compare', Scheduled: 'schedule', Assigned: 'assign', Attached: 'attach', Linked: 'link',
};

const ACRONYMS: Record<string, string> = {
  ams: 'AMS', id: 'ID', ids: 'IDs', sql: 'SQL', api: 'API', pdf: 'PDF', crm: 'CRM',
  url: 'URL', coi: 'COI', mvr: 'MVR', acord: 'ACORD', sms: 'SMS', naics: 'NAICS',
};

// Input keys that usually name the thing being acted on, best first.
const SUBJECT_KEYS = [
  'name', 'insured_name', 'insuredname', 'customer_name', 'client_name', 'customer', 'client', 'insured',
  'policy_number', 'policynumber', 'quote_number', 'search', 'searchtext', 'search_text', 'keyword', 'term',
  'query', 'q', 'email', 'to', 'recipient', 'subject', 'title', 'carrier', 'file_name', 'filename',
];

const words = (s: string): string[] =>
  s.replace(/([a-z])([A-Z])/g, '$1_$2').split(/[_\-\s.]+/).filter(Boolean).map(w => w.toLowerCase());

const pretty = (w: string): string => ACRONYMS[w] ?? w;

const titleCase = (s: string): string =>
  words(s).map(w => ACRONYMS[w] ?? w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

/** "mcp__nowcerts__search_insureds" -> { server: "nowcerts", tool: "search_insureds" } */
export function splitToolName(raw: string): { server?: string; tool: string } {
  const name = (raw || '').trim();
  const mcp = name.match(/^mcp__([^_]+(?:_[^_]+)*?)__(.+)$/);
  if (mcp) return { server: mcp[1], tool: mcp[2] };
  const dotted = name.match(/^([\w-]+)[.:/]([\w-]+)$/);
  if (dotted) return { server: dotted[1], tool: dotted[2] };
  return { tool: name };
}

/** Human label for an input key: "policy_number" -> "Policy number". */
export function humanizeKey(key: string): string {
  const w = words(key).map(pretty);
  if (!w.length) return key;
  const first = w[0];
  return [first === first.toUpperCase() ? first : first.charAt(0).toUpperCase() + first.slice(1), ...w.slice(1)].join(' ');
}

function parse(str: string): any | null {
  if (!str || typeof str !== 'string') return null;
  try { return JSON.parse(str); } catch { return null; }
}

function clip(s: string, n: number): string {
  const t = s.replace(/\s+/g, ' ').trim();
  return t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t;
}

/** Strips markdown so a result's first line reads as a sentence. */
function plainFirstLine(text: string): string {
  const line = text.split('\n').map(l => l.trim()).find(l => l && !/^[-|=:#\s]+$/.test(l)) ?? '';
  return line.replace(/^#+\s*/, '').replace(/[*_`>]/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').trim();
}

function subjectFrom(input: any): { key: string; value: string } | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
  const entries = Object.entries(input).map(([k, v]) => [k.toLowerCase(), v] as const);
  for (const want of SUBJECT_KEYS) {
    const hit = entries.find(([k]) => k === want);
    if (!hit) continue;
    const v = hit[1];
    if (typeof v !== 'string' && typeof v !== 'number') continue;
    const s = String(v).trim();
    // Skip raw SQL / long payloads — they aren't a "subject".
    if (!s || s.length > 48 || /^\s*(select|with|insert|update|delete)\b/i.test(s)) continue;
    return { key: want, value: s };
  }
  return null;
}

function countOf(r: any): number | null {
  if (Array.isArray(r)) return r.length;
  if (!r || typeof r !== 'object') return null;
  const odata = r['@odata.count'] ?? r.data?.['@odata.count'];
  if (typeof odata === 'number') return odata;
  for (const k of ['value', 'data', 'results', 'items', 'records', 'rows']) {
    if (Array.isArray(r[k])) return r[k].length;
    if (r[k] && typeof r[k] === 'object' && Array.isArray(r[k].value)) return r[k].value.length;
  }
  for (const k of ['count', 'total', 'totalCount', 'total_count']) {
    if (typeof r[k] === 'number') return r[k];
  }
  return null;
}

function describeAction(rawTool: string, input: any, ok: boolean): string {
  const { tool } = splitToolName(rawTool);
  const base = tool.replace(/(_tool|Tool)$/, '');
  const key = words(base).join('_');
  const subject = subjectFrom(input);
  const tail = subject
    ? (subject.key === 'to' || subject.key === 'recipient' || subject.key === 'email' ? ` to ${subject.value}` : ` for “${subject.value}”`)
    : '';

  // Built-in steps describe themselves; their inputs aren't a "subject".
  if (KNOWN_TOOLS[key]) return ok ? KNOWN_TOOLS[key] : `Couldn’t finish: ${KNOWN_TOOLS[key].toLowerCase()}`;

  const phrase = (verb: string, noun: string) =>
    ok ? `${verb}${noun ? ` ${noun}` : ''}${tail}` : `Tried to ${PRESENT[verb] ?? verb.toLowerCase()}${noun ? ` ${noun}` : ''}${tail}`;
  const w = words(base);
  if (w.length && VERBS[w[0]]) return phrase(VERBS[w[0]], w.slice(1).map(pretty).join(' '));
  // Noun-first names ("insured_search", "policy_lookup").
  const last = w[w.length - 1];
  if (w.length > 1 && VERBS[last]) return phrase(VERBS[last], w.slice(0, -1).map(pretty).join(' '));
  return `${ok ? 'Used' : 'Tried to use'} ${titleCase(base) || 'a tool'}${tail}`;
}

function describeOutcome(result: string, ok: boolean, running: boolean): string {
  if (running) return 'Working on it…';
  const r = parse(result);
  if (!ok) {
    const msg = r && typeof r === 'object' ? (r.error?.message ?? r.error ?? r.message ?? r.detail) : null;
    const text = typeof msg === 'string' ? msg : plainFirstLine(result || '');
    return text ? `Didn’t go through: ${clip(text, 90)}` : 'Didn’t go through';
  }
  const empty = !result?.trim()
    || (r !== null && typeof r === 'object' && (Array.isArray(r) ? r.length === 0 : Object.keys(r).length === 0));
  if (empty) return 'Nothing came back';
  if (r !== null) {
    const n = countOf(r);
    if (n !== null) return n === 0 ? 'No matches found' : `Found ${n.toLocaleString()} ${n === 1 ? 'result' : 'results'}`;
    if (typeof r === 'object') {
      for (const k of ['response', 'summary', 'message', 'answer', 'text', 'output']) {
        if (typeof r[k] === 'string' && r[k].trim()) return clip(plainFirstLine(r[k]), 110);
      }
      if (r.success === true || r.ok === true) return 'Done';
      if (typeof r.intent === 'string') return `Looks like: ${titleCase(r.intent).toLowerCase()}`;
      if (typeof r.route === 'string') return `Sent down the ${titleCase(r.route).toLowerCase()}`;
    }
    if (typeof r === 'string' || typeof r === 'number') return clip(String(r), 110);
    return 'Done';
  }
  const tableRows = result.split('\n').filter(l => /^\s*\|.*\|\s*$/.test(l) && !/^\s*\|[\s:|-]+\|\s*$/.test(l));
  if (tableRows.length > 1) {
    const n = tableRows.length - 1; // first row is the header
    return `Returned a table with ${n.toLocaleString()} ${n === 1 ? 'row' : 'rows'}`;
  }
  const line = plainFirstLine(result);
  return line ? clip(line, 110) : 'Done';
}

const OK_STATUSES = new Set(['success', 'succeeded', 'done', 'completed', 'complete', 'ok', 'approved']);
const RUNNING_STATUSES = new Set(['running', 'pending', 'in_progress', 'inprogress', 'started', 'queued']);

export function summarizeToolStep(exec: { tool: string; action: string; result: string; status: string }): ToolStepSummary {
  const status = (exec.status || '').toLowerCase();
  const running = RUNNING_STATUSES.has(status);
  const ok = running || OK_STATUSES.has(status) || !status;
  const { server } = splitToolName(exec.tool);
  return {
    title: describeAction(exec.tool, parse(exec.action), ok),
    outcome: describeOutcome(exec.result, ok, running),
    source: server ? titleCase(server) : undefined,
    ok,
    running,
  };
}
